import React, { useEffect, useRef } from 'react';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { Box, Typography, CircularProgress, Button, Tooltip } from '@mui/material';
import styles from './MessagesContainer.module.scss';
import cx from 'classnames';
import { MarkdownContent } from 'new/elements/MarkdownContent';
import { ReactComponent as BlackNexlaLogoIcon } from '../assets/nexla-logo-black.svg';
import { ReactComponent as ErrorIcon } from 'new/components/icons/exclamation.svg';
import { CodeDiffViewer } from './CodeDiffViewer/CodeDiffViewer';
import DateUtils from 'js/lib/DateUtils';
import { ReactComponent as ClockIcon } from 'new/components/icons/clock-chat-bot.svg';

export const GEN_AI_MESSAGE_TYPE = Object.freeze({
  TEXT: 'text',
  CODE: 'code',
  DSL_CODE: 'dsl_code',
  ERROR: 'error',
});

export interface Message {
  id: number;
  text?: string;
  isCode?: boolean;
  isUser?: boolean;
  rationale?: string; // rationale for the message provided by the AI
  messageFromAI?: string; // message from the AI, must be an user friendly language
  language?: string; // language of the code to be used for the syntax highlighter
  data?: string | string[]; // data to be used for the message, can be used to show the diff
  context?: any; // reference to the input context, can be used as the previous value to show the diff
  createdAt?: string; // timestamp of the message
  type?: (typeof GEN_AI_MESSAGE_TYPE)[keyof typeof GEN_AI_MESSAGE_TYPE]; // type of the message, can be used to render the message differently
}

interface MessagesContainerProps {
  messages: Message[];
  loading: boolean;
  error: string | null;
  applySuggestions: (data: Message['data']) => void;
  noCodeInteraction?: boolean;
}

const syntaxHighlighterStyle = {
  borderRadius: '4px',
  fontSize: '12px',
  marginBottom: '8px',
};

const getApplySuggestionsButton = (onClick: () => void, type: string) => {
  let tooltipTitle = 'Click to apply the transform code.';
  if (type === GEN_AI_MESSAGE_TYPE.DSL_CODE) {
    tooltipTitle = 'Click to apply the DSL code.';
  }

  return (
    <Box className={styles.applyCodeButtonContainer}>
      <Tooltip arrow title={tooltipTitle} placement="top">
        <Button
          variant="outlined"
          size="small"
          onClick={onClick}
          className={styles.applyCodeButton}
        >
          Apply
        </Button>
      </Tooltip>
    </Box>
  );
};

const formatText = (text: string): string => {
  if (!text) return '';

  // handle escaped newlines in the text
  let formattedText = text.replace(/\\n\\n/g, '\n\n').replace(/\\n/g, '\n');

  // handle hard line breaks in the text
  return formattedText
    .split('\n')
    .map((line) => line.trimEnd() + '  ')
    .join('\n');
};

const getMessageTimestamp = (createdAt) => {
  if (!createdAt) return null;

  const messageDate = new Date(createdAt);
  const today = new Date();
  const isToday = messageDate.toDateString() === today.toDateString();

  const timeFormat = isToday ? 'HH:mm a' : 'HH:mm a, MMM d';

  return (
    <Typography variant="caption" component="span" className={styles.messageTimestamp}>
      <ClockIcon />
      {DateUtils.getDateString(createdAt, timeFormat)}
    </Typography>
  );
};

const MessagesContainer: React.FC<MessagesContainerProps> = ({
  messages,
  loading,
  error,
  applySuggestions,
  noCodeInteraction,
}) => {
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const scrollToBottom = () => {
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTo({
          top: messagesContainerRef.current.scrollHeight,
          behavior: 'instant',
        });
      }
    };

    scrollToBottom(); // immediate scroll
    const timeoutId = setTimeout(scrollToBottom, 100); // additional scroll after a short delay to handle dynamic content rendering
    return () => clearTimeout(timeoutId);
  }, [messages, loading, error, messagesContainerRef]);

  const renderMessageContent = (message: Message, index: number) => {
    const text = message.text ?? '';
    const data = message.data ?? [];
    const segments = text.split(/(```[\s\S]*?```)/g);

    const messageKey = `message-${message.id}-${index}`;

    if (message.type === GEN_AI_MESSAGE_TYPE.DSL_CODE && data) {
      const context = Array.isArray(message?.context?.input_context)
        ? message.context.input_context.join('\n')
        : '';

      if (Array.isArray(data)) {
        return (
          <Box key={messageKey} gap={1} className={styles.codeContainer}>
            {message?.rationale && (
              <Typography variant="body1" component="span" className={styles.messageText}>
                {message?.rationale}
              </Typography>
            )}

            <CodeDiffViewer oldCode={context} newCode={data.join('\n')} />

            {!noCodeInteraction &&
              getApplySuggestionsButton(() => applySuggestions(data), GEN_AI_MESSAGE_TYPE.DSL_CODE)}
            {getMessageTimestamp(message?.createdAt)}
          </Box>
        );
      }
    }

    if (message.type === GEN_AI_MESSAGE_TYPE.CODE && message.isCode) {
      const code = text.slice(3, -3); // remove the backticks
      const language = message?.language || 'python';

      if (typeof code === 'string') {
        return (
          <Box key={messageKey} className={styles.codeContainer}>
            <SyntaxHighlighter language={language} customStyle={syntaxHighlighterStyle}>
              {code}
            </SyntaxHighlighter>
            {getApplySuggestionsButton(() => applySuggestions(code), GEN_AI_MESSAGE_TYPE.CODE)}
            {getMessageTimestamp(message?.createdAt)}
          </Box>
        );
      }
    }

    return segments.map((segment, segmentIndex) => {
      const formattedText = formatText(segment);

      return (
        <Typography
          key={`${segmentIndex}-${messageKey}`}
          variant="body1"
          component="span"
          className={styles.messageText}
        >
          <MarkdownContent>{formattedText}</MarkdownContent>
          {getMessageTimestamp(message?.createdAt)}
        </Typography>
      );
    });
  };

  return (
    <Box
      ref={messagesContainerRef}
      className={styles.messagesContainer}
      onMouseDown={(e) => e.stopPropagation()}
      style={{ cursor: loading ? 'wait' : 'auto' }}
    >
      {messages.map((message, index) => (
        <Box
          key={`${message.id}-${index}`}
          className={cx(styles.message, {
            [styles.userMessage]: message.isUser,
            [styles.aiMessage]: !message.isUser,
          })}
        >
          {!message.isUser && (
            <Box className={styles.blackLogo}>
              <BlackNexlaLogoIcon />
            </Box>
          )}

          <Box>{renderMessageContent(message, index)}</Box>
        </Box>
      ))}

      {loading ? (
        <Box className={styles.loadingContainer}>
          <CircularProgress size={20} thickness={5} color="inherit" />
          <Typography variant="body1" component="span" className={styles.messageText}>
            Hang tight! We're executing the step and it will take a moment.
          </Typography>
        </Box>
      ) : null}

      {error ? (
        <Box className={styles.errorContainer}>
          <ErrorIcon className={styles.errorIcon} />

          <Typography variant="body1" component="span" className={styles.errorMessage}>
            {error}
          </Typography>
        </Box>
      ) : null}
    </Box>
  );
};

export default MessagesContainer;
